import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg, downloadPdf } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RagDot, KpiCard, StatusBadge, PageHeader } from "../components/bits";
import { ArrowLeft, LogOut, FileDown } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PortfolioPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/portfolio").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const exportPdf = async () => {
    setExporting(true);
    try { await downloadPdf("/export/portfolio", "Portfolio-Report.pdf"); toast.success("Portfolio PDF downloaded"); }
    catch (e) { toast.error(errMsg(e)); }
    setExporting(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/projects" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /></Link>
          <img src="/logo-icon.png" alt="" className="h-8 w-8" />
          <div className="font-display font-black tracking-tighter text-lg">Ganttastic<span className="text-[#1E6FE8]">.app</span></div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">{user?.tenantName}</span>
          <button data-testid="logout-btn" onClick={logout} className="p-1.5 hover:text-red-600"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="portfolio-page">
        <div className="flex items-start justify-between">
          <PageHeader overline={`${user?.tenantName} · Cross-project rollup`} title="Portfolio Dashboard" />
          <Button data-testid="portfolio-export-pdf-btn" variant="outline" className="rounded-none" onClick={exportPdf} disabled={exporting}>
            <FileDown className="h-4 w-4 mr-1.5" /> {exporting ? "Exporting…" : "Export PDF"}
          </Button>
        </div>
        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <KpiCard testId="pf-projects" label="Projects" value={data.totals.projects}
                sub={`${data.totals.ragCounts.Green}G · ${data.totals.ragCounts.Amber}A · ${data.totals.ragCounts.Red}R`} />
              <KpiCard testId="pf-budget" label="Total Budget" value={`$${Number(data.totals.budget).toLocaleString()}`} />
              <KpiCard testId="pf-actual" label="Actual to Date" value={`$${Number(data.totals.actual).toLocaleString()}`}
                sub={`Forecast $${Number(data.totals.forecast).toLocaleString()}`} />
              <KpiCard testId="pf-risks" label="Open Risks" value={data.totals.openRisks} sub={`${data.totals.highRisks} high priority`} />
              <KpiCard testId="pf-overdue" label="Overdue Tasks" value={data.totals.overdueTasks} />
            </div>
            <div className="border border-border bg-card overflow-auto">
              <table className="w-full data-table text-sm" style={{ minWidth: 1100 }}>
                <thead>
                  <tr><th>Project</th><th>Health</th><th>Method</th><th>Overall</th><th>Sched</th><th>Budget RAG</th><th>Scope</th>
                    <th>Progress</th><th>Overdue</th><th>Risks (H)</th><th>CRs</th><th>Budget</th><th>Actual</th><th>Go-Live</th></tr>
                </thead>
                <tbody>
                  {data.projects.map((p) => (
                    <tr key={p.id} data-testid="portfolio-row">
                      <td><Link to={`/projects/${p.id}/dashboard`} className="font-medium text-[#1E6FE8] hover:underline">{p.name}</Link>
                        <div className="text-[10px] text-muted-foreground font-mono2">{p.code}</div></td>
                      <td data-testid="portfolio-health-cell">
                        {p.healthScore != null
                          ? <span className={`font-display font-black ${p.healthScore >= 70 ? "text-emerald-600" : p.healthScore >= 55 ? "text-amber-600" : "text-red-600"}`}>{p.healthScore}<span className="text-[10px] font-bold ml-0.5">{p.healthGrade}</span></span>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td><StatusBadge value={p.methodology === "Pmbok" ? "PMBOK" : p.methodology} tone="blue" /></td>
                      <td><RagDot value={p.overallRag} /></td>
                      <td><RagDot value={p.scheduleRag} /></td>
                      <td><RagDot value={p.budgetRag} /></td>
                      <td><RagDot value={p.scopeQualityRag} /></td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-16 bg-neutral-100"><div className="h-2 bg-[#1E6FE8]" style={{ width: `${p.avgPercentComplete}%` }} /></div>
                          <span className="text-xs">{p.avgPercentComplete}%</span>
                        </div>
                      </td>
                      <td>{p.overdueTasks > 0 ? <StatusBadge value={p.overdueTasks} tone="red" /> : "0"}</td>
                      <td>{p.openRisks} ({p.highRisks})</td>
                      <td>{p.openChangeRequests}</td>
                      <td className="whitespace-nowrap">${Number(p.budget).toLocaleString()}</td>
                      <td className="whitespace-nowrap">${Number(p.actual).toLocaleString()}</td>
                      <td className="text-xs whitespace-nowrap">{p.targetGoLiveDate || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
